import { Button, CheckboxField, TextField } from "@dash/form";
import { Dialog } from "@dash/dialog";
import { useToast } from "@dash/toast";
import { useTheme } from "@emotion/react";
import { useState } from "react";
import { MdCancel } from "react-icons/md";
import { useQueryClient } from "react-query";

import { Order } from "@megaron/fulfillment-contracts";
import { useClientManager } from "@megaron/react-clients";

import { CancelShopifyReasonField, type ShopifyCancelReason } from "./CancelShopifyReasonField";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  order: Order;
  queryKey: string | string[];
};

const formatCancelMutationError = (err: unknown) => {
  if (err instanceof Error) return err.message;
  return String(err);
};

export const CancelShopifyOrderDialog = ({ isOpen, onClose, order, queryKey }: Props) => {
  const theme = useTheme();
  const toast = useToast();
  const queryClient = useQueryClient();
  const cancelMutation = useClientManager("fulfillment").cancelShopifyOrder().useMutation();
  const rewardLineCount = order.items.filter((i) => i.purchaseId).length;
  const [reason, setReason] = useState<ShopifyCancelReason>("CUSTOMER");
  const [notifyCustomer, setNotifyCustomer] = useState(true);
  const [restock, setRestock] = useState(true);
  const [refundOriginalPayment, setRefundOriginalPayment] = useState(true);
  const [staffNote, setStaffNote] = useState("");
  const [confirmIrreversible, setConfirmIrreversible] = useState(false);

  if (!isOpen) return null;

  const handleCancel = () => {
    cancelMutation.mutate(
      {
        orderId: order.id,
        confirmIrreversible,
        notifyCustomer,
        restock,
        refundOriginalPayment,
        reason,
        staffNote: staffNote.trim() === "" ? null : staffNote.trim(),
      },
      {
        onSuccess: async (data) => {
          const now = new Date();
          queryClient.setQueryData<Order | undefined>(queryKey, (prev) =>
            prev ? { ...prev, cancelledAt: now } : prev,
          );
          queryClient.setQueriesData<Order | undefined>(
            { queryKey: ["fulfillment", "getOrder"], exact: false },
            (prev) => (prev && prev.id === order.id ? { ...prev, cancelledAt: now } : prev),
          );
          await Promise.allSettled([
            queryClient.invalidateQueries(queryKey),
            queryClient.invalidateQueries({ queryKey: ["fulfillment", "getOrder"] }),
            queryClient.invalidateQueries({ queryKey: ["fulfillment", "ordersQuery"] }),
            queryClient.refetchQueries(queryKey, { active: true }),
            queryClient.refetchQueries(["fulfillment", "getOrder"], { active: true }),
          ]);
          if (data.rewardsRefunded > 0) {
            toast.info(`Zamówienie anulowane. Zwrócono punkty za ${data.rewardsRefunded} zakup nagrody.`);
          } else {
            toast.info("Zamówienie anulowane w Shopify.");
          }
          onClose();
        },
        onError: (err) => {
          toast.error("Nie udało się anulować zamówienia", formatCancelMutationError(err));
        },
      },
    );
  };

  return (
    <Dialog
      header={
        <div css={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <MdCancel />
          Zwrot / anulowanie (Shopify)
        </div>
      }
      onClose={onClose}
      css={{ maxWidth: "520px" }}
    >
      <div css={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
        <div css={{ fontSize: "0.8125rem", lineHeight: 1.5 }}>
          Anuluje całe zamówienie w Shopify (nieodwracalne). Przy pozycjach z nagrodą i zsynchronizowanym zakupie punkty
          wracają na konto użytkownika z zamówienia.
        </div>
        {rewardLineCount > 0 && order.userId && (
          <div css={{ fontSize: "0.8125rem" }}>
            Wykryto {rewardLineCount} poz. z nagrodą — po anulowaniu nastąpi zwrot punktów.
          </div>
        )}
        {rewardLineCount > 0 && !order.userId && (
          <div css={{ fontSize: "0.8125rem", color: theme.colors.danger }}>
            Są pozycje z nagrodą, ale brak użytkownika na zamówieniu — zwrot punktów nie zostanie wykonany automatycznie.
          </div>
        )}

        <CancelShopifyReasonField value={reason} onChange={setReason} />

        <CheckboxField label="Powiadom klienta o anulowaniu" value={notifyCustomer} onChange={setNotifyCustomer} />
        <CheckboxField label="Zwróć towar na stan magazynowy" value={restock} onChange={setRestock} />
        <CheckboxField
          label="Zwrot na pierwotną metodę płatności"
          value={refundOriginalPayment}
          onChange={setRefundOriginalPayment}
        />

        <TextField label="Notatka wewnętrzna (opcjonalnie)" value={staffNote} onChange={setStaffNote} />

        <CheckboxField
          label="Rozumiem, że anulowanie zamówienia w Shopify jest nieodwracalne"
          value={confirmIrreversible}
          onChange={setConfirmIrreversible}
        />

        <div css={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
          <Button variant="outline" onClick={onClose}>
            Zamknij
          </Button>
          <Button
            color="danger"
            onClick={handleCancel}
            isLoading={cancelMutation.isLoading}
            isDisabled={!confirmIrreversible}
          >
            Anuluj zamówienie w Shopify
          </Button>
        </div>
      </div>
    </Dialog>
  );
};
